import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  MenuItem,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useForm, Controller } from 'react-hook-form';
import { useEvents } from '../../contexts/EventContext';
import { EventFormData } from '../../types';
import { useCategories } from '../../hooks/useCategories';
import { ROUTES } from '../../utils/constants';
import api from '../../services/api';

const EditEventPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getEvent, updateEvent } = useEvents();
  const { categories } = useCategories();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [locationOptions, setLocationOptions] = useState<string[]>([]);

  useEffect(() => {
    api.get('/locations').then((res) => {
      setLocationOptions(res.data.map((loc: any) => loc.name));
    }).catch(() => {});
  }, []);

  const event = id ? getEvent(id) : undefined;

  const [formInitialized, setFormInitialized] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<EventFormData>();

  useEffect(() => {
    if (event && !formInitialized) {
      reset({
        title: event.title,
        description: event.description,
        date: event.date,
        startTime: event.startTime,
        endTime: event.endTime,
        location: event.location,
        capacity: event.capacity,
        category: event.category,
        registrationDeadline: event.registrationDeadline,
        registrationDeadlineTime: event.registrationDeadlineTime || '23:59',
        isActive: event.isActive,
      });
      setFormInitialized(true);
    }
  }, [event, reset, formInitialized]);

  if (!event) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" gutterBottom>
          Event not found
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(ROUTES.ADMIN_EVENTS)}>
          Back to Events
        </Button>
      </Box>
    );
  }

  const onSubmit = async (data: EventFormData) => {
    if (!id) return;

    setIsSubmitting(true);

    try {
      await updateEvent(id, data);
      setShowSuccess(true);
      setTimeout(() => {
        navigate(ROUTES.ADMIN_EVENTS);
      }, 1500);
    } catch (err) {
      console.error('Failed to update event:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(ROUTES.ADMIN_EVENTS)}
        sx={{ mb: 3 }}
      >
        Back to Events
      </Button>

      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Edit Event
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Update the event details
      </Typography>

      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Event Title"
                  {...register('title', { required: 'Title is required' })}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  {...register('description', { required: 'Description is required' })}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  defaultValue={event.category}
                  {...register('category', { required: 'Category is required' })}
                  error={!!errors.category}
                  helperText={errors.category?.message}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="location"
                  control={control}
                  rules={{ required: 'Location is required' }}
                  render={({ field }) => (
                    <Autocomplete
                      freeSolo
                      options={locationOptions}
                      value={field.value || ''}
                      onChange={(_e, newValue) => field.onChange(newValue || '')}
                      onInputChange={(_e, newValue) => field.onChange(newValue || '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Location"
                          error={!!errors.location}
                          helperText={errors.location?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              {/* Date & Time */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                  Date & Time
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Event Date"
                  InputLabelProps={{ shrink: true }}
                  {...register('date', { required: 'Date is required' })}
                  error={!!errors.date}
                  helperText={errors.date?.message}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  InputLabelProps={{ shrink: true }}
                  {...register('startTime', { required: 'Start time is required' })}
                  error={!!errors.startTime}
                  helperText={errors.startTime?.message}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  InputLabelProps={{ shrink: true }}
                  {...register('endTime', { required: 'End time is required' })}
                  error={!!errors.endTime}
                  helperText={errors.endTime?.message}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Registration Deadline"
                  InputLabelProps={{ shrink: true }}
                  {...register('registrationDeadline', { required: 'Deadline is required' })}
                  error={!!errors.registrationDeadline}
                  helperText={errors.registrationDeadline?.message}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="time"
                  label="Deadline Time"
                  InputLabelProps={{ shrink: true }}
                  {...register('registrationDeadlineTime')}
                  helperText="Default: 11:59 PM"
                />
              </Grid>

              {/* Capacity */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                  Capacity & Settings
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Capacity"
                  {...register('capacity', {
                    required: 'Capacity is required',
                    min: { value: 1, message: 'Must be at least 1' },
                    valueAsNumber: true,
                  })}
                  error={!!errors.capacity}
                  helperText={errors.capacity?.message || `Current attendees: ${event.currentAttendees}`}
                  InputProps={{ inputProps: { min: event.currentAttendees || 1 } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="isActive"
                  control={control}
                  defaultValue={event.isActive}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          color="primary"
                        />
                      }
                      label="Event is active and visible to users"
                    />
                  )}
                />
              </Grid>

              {/* Submit */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate(ROUTES.ADMIN_EVENTS)}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={showSuccess}
        autoHideDuration={1500}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Event updated successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditEventPage;
